import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Popper,
  Fade,
  ClickAwayListener,
  Divider,
  Chip,
  Typography,
} from "@mui/material";

const ResearchModeSettings = ({
  open,
  anchorEl,
  researchPreference,
  onClose,
  onSaveResearchPreference,
}) => {
  const [draftMode, setDraftMode] = useState("DESIGN_INTELLIGENCE");
  const [draftConversionSource, setDraftConversionSource] = useState("CIRCANA");
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const [preferenceError, setPreferenceError] = useState("");

  React.useEffect(() => {
    if (!researchPreference?.research_mode) return;
    setDraftMode(researchPreference.research_mode);
    setDraftConversionSource(researchPreference.conversion_source || "CIRCANA");
  }, [researchPreference]);

  const handleClose = () => {
    setPreferenceError("");
    onClose();
  };

  const currentMode = researchPreference?.research_mode || "DESIGN_INTELLIGENCE";
  const currentConversionSource = researchPreference?.conversion_source || "CIRCANA";

  const hasPreferenceChanged =
    draftMode !== (researchPreference?.research_mode || "DESIGN_INTELLIGENCE") ||
    (draftMode === "ALL_SOURCE" &&
      draftConversionSource !== (researchPreference?.conversion_source || "CIRCANA"));

  const handleSavePreference = async () => {
    setPreferenceError("");
    if (draftMode === "ALL_SOURCE" && !draftConversionSource) {
      setPreferenceError("Please select Circana or SPINS as conversion data source.");
      return;
    }

    if (!onSaveResearchPreference) {
      handleClose();
      return;
    }

    setIsSavingPreference(true);
    try {
      const payload = {
        research_mode: draftMode,
        conversion_source: draftMode === "ALL_SOURCE" ? draftConversionSource : null,
      };
      const response = await onSaveResearchPreference(payload);

      if (response?.success) {
        handleClose();
        return;
      }

      setPreferenceError(response?.message || "Unable to save preference. Please try again.");
    } catch (error) {
      setPreferenceError("Unable to save preference. Please try again.");
    } finally {
      setIsSavingPreference(false);
    }
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom"
      transition
      disablePortal
      sx={{ zIndex: 1400 }}
      modifiers={[
        { name: "offset", options: { offset: [0, 14] } },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={170}>
          <Box>
            <ClickAwayListener onClickAway={handleClose}>
              <Paper
                elevation={0}
                sx={{
                  position: "relative",
                  width: 390,
                  borderRadius: "12px",
                  border: "1px solid #d7deef",
                  p: 2.25,
                  background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
                  boxShadow: "0 16px 36px rgba(12, 28, 66, 0.18)",
                  overflow: "visible",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -8,
                    left: "calc(50% - 7px)",
                    width: 14,
                    height: 14,
                    transform: "rotate(45deg)",
                    borderLeft: "1px solid #d7deef",
                    borderTop: "1px solid #d7deef",
                    backgroundColor: "#ffffff",
                    zIndex: 1,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: "1.1rem", color: "#152a66" }}>
                    Research Mode
                  </Typography>
                  <Chip
                    label={
                      draftMode === "ALL_SOURCE"
                        ? "All Source"
                        : draftMode === "CONSUMER_SENTIMENT"
                        ? "Consumer Sentiment"
                        : "Design Intelligence"
                    }
                    size="small"
                    sx={{
                      fontWeight: 700,
                      color: "#18307a",
                      backgroundColor: "rgba(33, 74, 181, 0.1)",
                    }}
                  />
                </Box>

                <Typography sx={{ fontSize: "0.8rem", color: "#5f6d93", mb: 1.75 }}>
                  Configure default data-source scope for all new responses.
                </Typography>

                <FormControl component="fieldset" sx={{ width: "100%" }}>
                  <RadioGroup value={draftMode} onChange={(e) => setDraftMode(e.target.value)}>
                    <Box
                      sx={{
                        border: draftMode === "DESIGN_INTELLIGENCE" ? "1px solid #9fb6f8" : "1px solid #e1e8fb",
                        borderRadius: "9px",
                        px: 1,
                        py: 0.6,
                        mb: 1,
                        backgroundColor: draftMode === "DESIGN_INTELLIGENCE" ? "#f5f8ff" : "#fff",
                        transition: "all 140ms ease",
                      }}
                    >
                      <FormControlLabel
                        value="DESIGN_INTELLIGENCE"
                        control={<Radio size="small" />}
                        label={<Typography sx={{ fontWeight: 700, color: "#162f73" }}>Design Intelligence</Typography>}
                      />
                      <Typography sx={{ fontSize: "0.78rem", color: "#6374a4", ml: 4.3, mt: -0.25, mb: 0.4 }}>
                        Tastewise, Mintel and Documents
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        border: draftMode === "CONSUMER_SENTIMENT" ? "1px solid #9fb6f8" : "1px solid #e1e8fb",
                        borderRadius: "9px",
                        px: 1,
                        py: 0.6,
                        mb: 1,
                        backgroundColor: draftMode === "CONSUMER_SENTIMENT" ? "#f5f8ff" : "#fff",
                        transition: "all 140ms ease",
                      }}
                    >
                      <FormControlLabel
                        value="CONSUMER_SENTIMENT"
                        control={<Radio size="small" />}
                        label={<Typography sx={{ fontWeight: 700, color: "#162f73" }}>Consumer Sentiment</Typography>}
                      />
                      <Typography sx={{ fontSize: "0.78rem", color: "#6374a4", ml: 4.3, mt: -0.25, mb: 0.4 }}>
                        SproutSocial, Yogi and Documents
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        border: draftMode === "ALL_SOURCE" ? "1px solid #9fb6f8" : "1px solid #e1e8fb",
                        borderRadius: "9px",
                        px: 1,
                        py: 0.6,
                        backgroundColor: draftMode === "ALL_SOURCE" ? "#f5f8ff" : "#fff",
                        transition: "all 140ms ease",
                      }}
                    >
                      <FormControlLabel
                        value="ALL_SOURCE"
                        control={<Radio size="small" />}
                        label={<Typography sx={{ fontWeight: 700, color: "#162f73" }}>All Source</Typography>}
                      />
                      <Typography sx={{ fontSize: "0.78rem", color: "#6374a4", ml: 4.3, mt: -0.25, mb: draftMode === "ALL_SOURCE" ? 0.6 : 0.4 }}>
                        Design Intelligence + Consumer Sentiment and Conversion Data(Circana OR SPINS)
                      </Typography>

                      {draftMode === "ALL_SOURCE" && (
                        <Box sx={{ ml: 4.1, mt: 0.4 }}>
                          <FormLabel component="legend" sx={{ fontSize: "0.74rem", color: "#162f73", mb: 0.35 }}>
                            Default Conversion Source
                          </FormLabel>
                          <RadioGroup
                            row
                            value={draftConversionSource}
                            onChange={(e) => setDraftConversionSource(e.target.value)}
                          >
                            <FormControlLabel
                              value="CIRCANA"
                              control={<Radio size="small" />}
                              label={<Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Circana</Typography>}
                            />
                            <FormControlLabel
                              value="SPINS"
                              control={<Radio size="small" />}
                              label={<Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>SPINS</Typography>}
                            />
                          </RadioGroup>
                        </Box>
                      )}
                    </Box>
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontSize: "0.76rem", color: "#7a86a8", mb: 1 }}>
                  <b>Current saved preference:</b> {
                    currentMode === "ALL_SOURCE"
                      ? "All Source"
                      : currentMode === "CONSUMER_SENTIMENT"
                      ? "Consumer Sentiment"
                      : "Design Intelligence"
                  }
                  {currentMode === "ALL_SOURCE" ? ` (${currentConversionSource})` : ""}
                </Typography>

                {preferenceError && (
                  <Typography sx={{ color: "error.main", mt: 0.2, fontSize: "0.82rem", mb: 1 }}>
                    {preferenceError}
                  </Typography>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSavingPreference}
                    sx={{
                      borderColor: "#c9d4f3",
                      color: "#32457f",
                      fontWeight: 700,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSavePreference}
                    disabled={isSavingPreference || !hasPreferenceChanged}
                    startIcon={
                      isSavingPreference ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    sx={{
                      fontWeight: 700,
                      boxShadow: "none",
                    }}
                  >
                    {isSavingPreference ? "Saving..." : "Save"}
                  </Button>
                </Box>
              </Paper>
            </ClickAwayListener>
          </Box>
        </Fade>
      )}
    </Popper>
  );
};

ResearchModeSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.object,
  researchPreference: PropTypes.shape({
    research_mode: PropTypes.string,
    conversion_source: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSaveResearchPreference: PropTypes.func,
};

export default ResearchModeSettings;
